import { component as arcadeComponentColors, semantic as arcadeSemanticColors } from '@arcade/web'

import afterpayColors from './afterpayColors'
import { cashAfterpayArcadeColors } from './cashAfterpayColors'
import { investingArcadeColors } from './investingColors'
import { localArcadeColors } from './localColors'
import { taxesArcadeColors } from './taxesColors'

const arcadeColors = {
  ...arcadeSemanticColors,
  ...arcadeComponentColors,
}

export type SemanticColors = keyof typeof arcadeSemanticColors
export type ComponentColors = keyof typeof arcadeComponentColors
export type ArcadeThemeVariant = 'default' | 'taxes' | 'bitcoin' | 'afterpay' | 'investing' | 'cashAfterpay' | 'local'
export type ArcadeColors = keyof typeof arcadeColors
export type ThemeColors = Record<ArcadeColors, ReturnType<typeof getArcadeThemeColor>>

export type ArcadeThemeColors = {
  semantic: { [key in SemanticColors]: string }
  component: { [key in ComponentColors]: string }
}

export type ArcadeThemeColorsOverride = { [key in ArcadeColors]: { light: string; dark: string } }
export const themeColorExists = (color: ArcadeColors) => arcadeColors[color] != null

const getVariantColors = ({
  themeVariant,
  darkMode,
}: {
  themeVariant: ArcadeThemeVariant
  darkMode: boolean
}): ArcadeThemeColors => {
  let themeColors: Partial<ArcadeThemeColorsOverride>

  switch (themeVariant) {
    case 'afterpay':
      themeColors = afterpayColors
      break
    case 'taxes':
      themeColors = taxesArcadeColors
      break
    case 'cashAfterpay':
      themeColors = cashAfterpayArcadeColors
      break
    case 'investing':
      themeColors = investingArcadeColors
      break
    case 'local':
      themeColors = localArcadeColors
      break
    default:
      themeColors = arcadeComponentColors
      break
  }

  return getArcadeThemeColors({ themeColors, isDark: darkMode })
}

export const getArcadeColorsByThemeVariant = ({
  themeVariant,
  darkMode,
}: {
  themeVariant: ArcadeThemeVariant
  darkMode: boolean
}): ArcadeThemeColors => {
  let themeVariantColors = getVariantColors({
    themeVariant,
    darkMode,
  })

  if (themeVariant === 'default') {
    return getBaseArcadeColors(darkMode)
  } else {
    const { semantic, component } = getBaseArcadeColors(darkMode)
    const { component: themeVariantComponent } = themeVariantColors

    themeVariantColors = { semantic, component: { ...component, ...themeVariantComponent } }
    return themeVariantColors
  }
}

export const getOverrideArcadeColorsByThemeVariant = ({
  themeVariant,
  darkMode,
}: {
  themeVariant: ArcadeThemeVariant
  darkMode: boolean
}): ArcadeThemeColors => {
  return getVariantColors({
    themeVariant,
    darkMode,
  })
}

const getBaseArcadeColors = (isDark: boolean) => {
  const getArcadeThemeColor = (
    themeColors: Partial<ArcadeThemeColorsOverride>,
    isDark: boolean,
    key: ArcadeColors,
    stack: string[] = []
  ): string => {
    if (stack.includes(key)) {
      /* eslint-disable-next-line */
      console.warn(`circular color reference [isDark:${isDark}]: [${[...stack, key].join('->')}]`)
      return key
    }

    const semanticColor = themeColors[key]

    const color = (isDark ? semanticColor?.dark : semanticColor?.light) as ArcadeColors
    if (themeColorExists(color)) {
      return getArcadeThemeColor(themeColors, isDark, color, [...stack, key])
    }

    return color
  }

  const semantic = Object.keys(arcadeSemanticColors).reduce((set, key) => {
    set[key as ArcadeColors] = getArcadeThemeColor(arcadeSemanticColors, isDark, key as ArcadeColors)
    return set
  }, {} as ThemeColors)

  const component = Object.keys(arcadeComponentColors).reduce((set, key) => {
    set[key as ComponentColors] = getArcadeThemeColor(arcadeComponentColors, isDark, key as ArcadeColors)
    return set
  }, {} as ThemeColors)

  return {
    semantic,
    component,
  }
}

export const getArcadeThemeColors = ({
  themeColors,
  isDark,
}: {
  themeColors: Partial<ArcadeThemeColorsOverride>
  isDark: boolean
}): ArcadeThemeColors => {
  const { semantic } = getBaseArcadeColors(isDark)

  const component = Object.keys(themeColors).reduce((set, key) => {
    set[key as ComponentColors] = getArcadeThemeColor(themeColors, isDark, key as ArcadeColors)
    return set
  }, {} as ThemeColors)

  return {
    semantic,
    component,
  }
}

const getArcadeThemeColor = (
  themeColors: Partial<ArcadeThemeColorsOverride>,
  isDark: boolean,
  key: ArcadeColors,
  stack: string[] = []
): string => {
  if (stack.includes(key)) {
    /* eslint-disable-next-line */
    console.warn(`circular color reference [isDark:${isDark}]: [${[...stack, key].join('->')}]`)
    return key
  }

  const semanticColor = themeColors[key]

  const color = (isDark ? semanticColor?.dark : semanticColor?.light) as ArcadeColors
  if (themeColorExists(color)) {
    return getArcadeThemeColor(themeColors, isDark, color, [...stack, key])
  }

  return color
}

export default arcadeColors
